import superagent from 'superagent';
import { startsWith, isFunction, each, isArray } from 'lodash';
import moment from 'moment';
import config from '../config';
import envConfig from '../../static/config';
import SessionStore from '../stores/SessionStore';
import InternetBankingActions from '../actions/InternetBankingActions';
import InternetBankingDataStore from '../stores/InternetBankingDataStore';
import SessionActionCreator from '../actions/SessionActionCreator';
import AppActions from '../actions/AppActions';
import AppStore from '../stores/AppStore';
import InternetBankingContentStore from '../stores/InternetBankingContentStore';
import BrowserUtils from '../utils/BrowserUtils';
import SiteHeaderActionCreator from '../actions/SiteHeaderActionCreator';

const getClientContext = () => {
  const clientContext = {
    client: {
      user_tracking_id: AppStore.getUserTrackingID(),
      app_title: InternetBankingContentStore.get('documentTitle'),
      app_package_name: config.appPackageName,
      app_version_code: config.version,
      app_version_name: config.versionName,
      client_id: envConfig.device_name,
    },
    env: {
      platform: config.platform,
      platform_version: BrowserUtils.getBrowserVersion(),
      make: BrowserUtils.getPlatform(),
      locale: BrowserUtils.getUserLocale(),
      device_name: envConfig.device_name,
    },
  };
  return JSON.stringify(clientContext);
};

const getBankId = (data) => {
  const bankId = SessionStore.getSessionBankID();
  if (bankId.toUpperCase() === 'DYB') {
    throw new Error('Invalid bank id');
  }
  return bankId;
};

const getCaseSubType = () => {
  return InternetBankingDataStore.getValue('isExistingCustomer') === 'Yes'
    ? 'existing'
    : 'ntb';
};

const _urlReplacements = {
  '{bank-id}': (data) => getBankId(data),
  '{case-subtype}': () => getCaseSubType(),
};

const ApiUtils = {
  /**
   * replace a placeholder in the URL
   * @param  {Object} 			data          Data coming from api call
   * @param  {String} 			replaceString A string that is to be replaced
   * @param  {return|String} 	replaceWith   Value that replaces replaceString occurances
   * @return {String}               			  Modified string
   */
  replaceUriString(data, replaceString, replaceWith) {
    if (data.url && data.url.indexOf(replaceString) > -1) {
      const value =
        isFunction(replaceWith) === true ? replaceWith(data) : replaceWith;

      if (!value) {
        return data.url;
      }

      const regex = new RegExp(replaceString, 'g');

      return data.url.replace(regex, value);
    }

    return data.url;
  },
  getApiClientContext(url = null) {
    return getClientContext(url);
  },

  /**
   * Make an API call.
   *
   * @param  {Object} callData 		All data about the call.
   * @param {Function} onSuccess 	Optional. No default. Callback when API call completes successfully.
   * @param {Function} onFail 	Optional. Default navigates user to error page. Callback when the API call fails.
   */
  makeAjaxCall(callData, onSuccess, onFail) {
    const data = callData;
    if (!navigator.onLine) {
      window.onbeforeunload = null;
      window.location.reload();
      return;
    }
    if (!data.apiVersion) {
      console.warn('API call requires a version for endpoint', data.url);
    }

    const contentType =
      data.contentType === 'N' ? null : data.contentType || 'application/json';

    // replace all placeholders in the url
    each(_urlReplacements, (replaceWith, replaceString) => {
      data.url = this.replaceUriString(data, replaceString, replaceWith);
    });

    const request = superagent(data.method, data.url)
      .set('Accept', 'application/json')
      .set('x-bpi-version', data.apiVersion)
      .set('x-bpi-service-context', config.authentication.context);

    if (data.headers) {
      each(data.headers, (value, key) => request.set(key, value));
    }

    if (contentType !== null) {
      request.set('Content-Type', contentType);
    }

    // Auth token present?
    if (data.addAuthToken) {
      const authToken =
        SessionStore.getURLTokenValue() ||
        data.authToken ||
        SessionStore.getToken()['access_token'];
      if (authToken) {
        request.set('Authorization', `Bearer ${authToken}`);
      }
    } else if (data.authorizationHeader) {
      request.set('Authorization', data.authorizationHeader);
    }

    request.set('x-bpi-client-context', getClientContext());

    // By default, alert the app any time we are making an API call.
    if (!data.doNotTrackCallProgress) {
      AppActions.trackApiCallStarted(data.url);
    }

    // Data to send?
    if (data.requestData) {
      request.send(data.requestData);
    }

    request.end((err, res) => {
      // Now log that the request is finished.
      if (!data.doNotTrackCallProgress) {
        AppActions.trackApiCallComplete(data.url);
      }
      if (err) {
        let authHeader;
        let errorResponse;
        const errorStatus = err.status;
        if (!navigator.onLine) {
          window.onbeforeunload = null;
          window.location.reload();
          return;
        }
        if (err.response) {
          authHeader = err.response.headers[config.authenticateHeaderName];
          if (
            authHeader === undefined &&
            errorStatus === 401 &&
            data.url.search('oauth2/token') < 0
          ) {
            errorResponse = {
              error: {
                code: 401,
              },
            };
          } else {
            errorResponse = err.response.body || err.response.text;
          }
        }
        if (
          startsWith(authHeader, 'Bearer realm error=tsandcs') &&
          errorStatus === 401
        ) {
          authHeader = undefined;
          errorResponse = err.response;
        } else if (
          startsWith(authHeader, 'Bearer realm error=userIsIneligible')
        ) {
          errorResponse = err.response;
          SiteHeaderActionCreator.hideHeaderFlag(true);
          InternetBankingActions.navigateToWebTask('WEB-NONBCUSTOMERERROR');
          return;
        } else if (
          startsWith(authHeader, 'Bearer realm scope=40') ||
          startsWith(authHeader, 'Bearer realm scope=35')
        ) {
          authHeader = 'default';
          const token = err.response.headers['x-bpi-verification-type'];
          const basicError = {
            code: 401,
          };
          const verification_type = token ? token : null;

          errorResponse = {
            error: Object.assign({}, basicError, { verification_type }),
          };
        }
        if (
          process.env.NODE_ENV === 'development' &&
          errorStatus === 401 &&
          data.url.indexOf('user/token') !== -1 &&
          err.response.body.error === 'Unauthorized'
        ) {
          window.sessionStorage.removeItem('devToken');
          SessionActionCreator.requestAccessTokenReset(true);
          window.location.reload(true);
        }
        // Check for the auth header to see if the error requires step up authentication
        if (authHeader !== undefined && authHeader !== 'default') {
          // If there is an authHeader, handle it and then exit.
          SessionActionCreator.requestAccessTokenReset(true);
          InternetBankingActions.navigateToWebTask(
            'WEB-INVALIDTOKENSESSIONTIMEOUT'
          );
          return;
        } else if (onFail) {
          if (
            errorResponse !== undefined &&
            errorResponse.error !== undefined &&
            errorResponse.error.hasOwnProperty('quoteId')
          ) {
            if (errorResponse.error.hasOwnProperty('id')) {
              errorResponse.error.quoteId = this.getQuoteId(
                errorResponse.error.id
              );
            } else {
              errorResponse.error.quoteId = ' ';
            }
          }
          onFail(errorResponse, errorStatus);
        } else {
          InternetBankingActions.navigateToWebTask('WEB-ERROR');
        }

        return;
      }

      if (onSuccess) {
        onSuccess(res.body, res.status, res.text);
      }
    });
  },

  /**
   * Extract the required scope from the WWW-Authenticate header
   *
   * @param  {String} header 		Value of WWW-Authenticate header
   *
   * @return {Number} the extracted scope or -1 if there isn't a parseable value
   */
  getRequiredScope(header) {
    if (header) {
      const numbersMatch = header.match(/(\d+)/);
      if (isArray(numbersMatch)) {
        return parseInt(numbersMatch[0]);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.error(
        '!! Response returned no scope, returning -1, possibly not a valid 403.  Value returned: ',
        header
      );
    }

    return -1;
  },

  setSessionValuesForStatements(month, account_id) {
    let beginDate;
    let endDate;
    const monthStartDay = 1;
    if (month === 0) {
      if (Number(monthStartDay) === Number(moment().format('DD'))) {
        beginDate = moment().subtract(1, 'M').date(1).format('YYYY-MM-DD');
      } else {
        beginDate = moment()
          .subtract(Number(month), 'M')
          .date(1)
          .format('YYYY-MM-DD');
      }
      endDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
    } else {
      beginDate = moment()
        .subtract(Number(month), 'M')
        .date(1)
        .format('YYYY-MM-DD');
      endDate = moment()
        .subtract(Number(month), 'M')
        .endOf('month')
        .format('YYYY-MM-DD');
    }
    const authToken =
      SessionStore.getURLTokenValue() ||
      SessionStore.getToken()['access_token'];
    const bank_id = SessionStore.getSessionBankID();
    const data = {
      bank_id,
      account_id,
      scope_id: 'default',
      from_date: beginDate,
      to_date: endDate,
      authorization: `Bearer ${authToken}`,
      // 'xbpi_version': '0.0.0',
      xbpi_version: config.apiVersions.getStatementServices,
      xbpi_service_context: config.authentication.context,
      xbpi_client_context: getClientContext(),
    };
    return data;
  },
  /**
   * Extract the quoteId from the error packet
   *
   * @param  {String} id from the error packet
   *
   * @return {String} the extracted last 6 digit of the id
   */
  getQuoteId(id) {
    if (id.length >= 6) {
      return id.slice(-6);
    } else {
      return id;
    }
  },
  makeXMLHttpRequest(callData) {
    const data = callData;
    // replace all placeholders in the url
    each(_urlReplacements, (replaceWith, replaceString) => {
      data.url = this.replaceUriString(data, replaceString, replaceWith);
    });
    const request = new XMLHttpRequest();
    request.open(data.method, data.url, false);
    request.setRequestHeader('Accept', 'application/json');
    request.setRequestHeader('x-bpi-version', data.apiVersion);
    request.setRequestHeader(
      'x-bpi-service-context',
      config.authentication.context
    );
    const contentType =
      data.contentType === 'N' ? null : data.contentType || 'application/json';
    if (contentType !== null) {
      request.setRequestHeader('Content-Type', contentType);
    }
    if (data.addAuthToken) {
      const authToken =
        SessionStore.getURLTokenValue() ||
        data.authToken ||
        SessionStore.getToken()['access_token'];
      if (authToken) {
        request.setRequestHeader('Authorization', `Bearer ${authToken}`);
      }
    } else if (data.authorizationHeader) {
      request.setRequestHeader('Authorization', data.authorizationHeader);
    }
    request.setRequestHeader('x-bpi-client-context', getClientContext());
    request.send();
    return {
      isSuccess: request.status,
      responseData: request.responseText,
    };
  },
  makeRequest(apiData, onSuccess, onFail) {
    const authToken =
      SessionStore.getURLTokenValue() ||
      SessionStore.getToken()['access_token'];
    const requestHeaders = {
      'x-bpi-client-context': getClientContext(),
      'x-bpi-version': '0.8.0',
      'x-bpi-service-context': 'USER',
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    };
    return fetch(
      apiData.url.replace('{bank-id}', SessionStore.getSessionBankID()),
      {
        body: JSON.stringify(apiData.requestData),
        method: 'POST',
        headers: requestHeaders,
      }
    )
      .then((response) =>
        response.blob().then((blob) => {
          const contentDis = response.headers.get('Content-Disposition');
          const contentDispositionHeaderResult = contentDis
            .split(';')[1]
            .trim()
            .split('=')[1];
          const contentDispositionFileName =
            contentDispositionHeaderResult.replace(/"/g, '');
          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, contentDispositionFileName);
          } else {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = contentDis
              ? contentDispositionFileName
              : 'AccountBalance.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }
        })
      )
      .catch((error) => {
        onFail(error);
      });
  },
};

ApiUtils.getClientContext = getClientContext;
ApiUtils.getBankId = getBankId;

export default ApiUtils;
